import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Switch from 'react-ios-switch';
import { useDebounce } from 'react-use';
import useAsyncEffect from 'use-async-effect';
import { InlineInputCardWrapper } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { AiComponentHtmlVideo, VideoAttributes } from '@/pkg/ai';
import { VideoTile } from '@/features/editor/widgets/custom-widget/inputs/background/background-video';
import { ImageTile } from '@/features/editor/widgets/custom-widget/inputs/background/image/shared/image-tile';
import { useVideoModals } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/html-video/use-video-modals';
import { InputModal } from '@/components/input-modal';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import Portal from '@/components/portal';
import { EditorContext } from '@/features/editor/context/editor-context';
import { uploadPosterImage } from '@/features/editor/widgets/custom-widget/inputs/background/video/utils';
import { useMediaApi } from '@/webapi/use-media-api';
import { PosterImageOverlay } from '@/features/editor/widgets/custom-widget/inputs/background/video/poster-image-overlay';

export interface HtmlVideoInputProps {
  component: AiComponentHtmlVideo;
  getVideoAttributes(selector: string): VideoAttributes;
  setVideoAttributes(selector: string, attrs: VideoAttributes): void;
}

function reloadVideo(
  iframeRef: React.MutableRefObject<HTMLIFrameElement>,
  component: AiComponentHtmlVideo,
) {
  const videoWidget = iframeRef?.current?.contentWindow?.document.querySelector(
    component.selector,
  ) as HTMLVideoElement;
  if (videoWidget) {
    setTimeout(() => videoWidget.load(), 200);
  }
}

export function HtmlVideoInput({
  component,
  getVideoAttributes,
  setVideoAttributes,
}: HtmlVideoInputProps) {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = React.useState(false);
  const shouldRefreshPoster = useRef(false);
  const [attributes, setAttributes] = React.useState<VideoAttributes>(
    getVideoAttributes(component.selector),
  );
  const { uploadImage } = useMediaApi();
  const {
    devicePreview: { iframeRef },
  } = useContext(EditorContext);
  const {
    showVideoModal,
    hideVideoModal,
    videoModalProps,
    posterModalProps,
    showPosterModal,
    posterImageRef,
    hidePosterModal,
  } = useVideoModals(videoRef);

  useDebounce(
    () => {
      setVideoAttributes(component.selector, attributes);
      reloadVideo(iframeRef, component);
    },
    500,
    [component, attributes],
  );

  const onVideoLoaded = async () => {
    setLoaded(true);
  };

  useEffect(() => {
    videoRef?.current?.addEventListener?.(`loadeddata`, onVideoLoaded, false);
    return () => {
      videoRef?.current?.removeEventListener?.(
        `loadeddata`,
        onVideoLoaded,
        false,
      );
    };
  }, []);

  useAsyncEffect(async () => {
    if (!loaded && shouldRefreshPoster.current) {
      const resp = await uploadPosterImage(videoRef, uploadImage);
      if (resp && resp.thumbnail) {
        setAttributes((attrs) => ({ ...attrs, poster: resp.thumbnail }));
        shouldRefreshPoster.current = false;
      }
    }
  }, [loaded]);

  useEffect(() => {
    const videoEl = videoRef.current as HTMLVideoElement | null;
    if (!videoEl) return;
    videoEl.loop = true;

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoEl.play();
          } else {
            videoEl.pause();
          }
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(videoEl);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [attributes?.source, loaded]);

  const onAutoplay = (checked: boolean) => {
    setAttributes((attrs) => ({ ...attrs, autoplay: checked }));
  };

  const onLoop = (checked: boolean) => {
    setAttributes((attrs) => ({ ...attrs, loop: checked }));
  };

  const onMuted = (checked: boolean) => {
    setAttributes((attrs) => ({ ...attrs, muted: checked }));
  };

  const onMediaControls = (checked: boolean) => {
    setAttributes((attrs) => ({ ...attrs, controls: checked }));
  };

  const onPoster = (url: string) => {
    setAttributes((attrs) => ({ ...attrs, poster: url }));
    hidePosterModal();
    shouldRefreshPoster.current = false;
  };

  const onVideoSelected = (url: string) => {
    setAttributes((attrs) => ({ ...attrs, source: url }));
    hideVideoModal();
    shouldRefreshPoster.current = true;
    setLoaded(false);
  };

  return (
    <Wrapper>
      <Portal selector="#editor-wrapper">
        <SharedElementOverlay {...videoModalProps}>
          <InputModal
            title="Link a video"
            description="Paste a link to the video you want to show"
            yesCaption="Update"
            noCaption="Cancel"
            initialValue={attributes?.source}
            onConfirm={onVideoSelected}
            onDiscard={hideVideoModal}
          />
        </SharedElementOverlay>
        <SharedElementOverlay {...posterModalProps}>
          <PosterImageOverlay
            videoRef={videoRef}
            onPosterImage={onPoster}
            hide={hidePosterModal}
          />
        </SharedElementOverlay>
      </Portal>
      <span>Video Link</span>
      <VideoTile
        mref={videoRef}
        src={attributes?.source}
        onClick={showVideoModal}
      />
      <span ref={posterImageRef}>Opening Image</span>
      <ImageTile
        key={attributes?.poster}
        src={attributes?.poster}
        onClick={showPosterModal}
      />
      <span>Autoplay</span>
      <SmallSwitch checked={attributes?.autoplay} onChange={onAutoplay} />
      <span>Loop Video</span>
      <SmallSwitch checked={attributes?.loop} onChange={onLoop} />
      <span>Mute Audio</span>
      <SmallSwitch checked={attributes?.muted} onChange={onMuted} />
      <span>Show Media Controls</span>
      <SmallSwitch checked={attributes?.controls} onChange={onMediaControls} />
    </Wrapper>
  );
}

const Wrapper = styled(InlineInputCardWrapper)`
  && {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr auto;
    gap: 3rem;

    img {
      margin-left: 0.7rem;
      max-width: 4.5rem;
    }
  }
`;

const SmallSwitch = styled(Switch)`
  && {
    justify-self: flex-end;
    transform: scale(0.8);
  }
`;
